import type { ThrustConversionEvent } from '@avisoma/shared';
import { SourcePageCell } from './SourcePageCell';
import { Td, Th, TruncatedTd } from './cells';
import { FindingMarkerCell, FindingMarkerHeader, type RowFindings } from './FindingMarkerCell';
import {
  extractionRowClass,
  extractionTableClass,
  extractionTbodyClass,
  extractionTheadClass,
} from './tableClasses';

type ThrustRow = ThrustConversionEvent & { pageId?: string };

/**
 * Dense grid for `thrustConversionEvents` (thrust rating change rows, DR-3261).
 * One row per conversion — a page can list several. Used on the entity view
 * (with a source-page column) and the page view (pass no `pageIdToPosition`).
 */
export function ThrustConversionTable({
  rows,
  pageIdToPosition,
  onInspect,
  rowFindings,
}: {
  rows: ThrustRow[];
  pageIdToPosition?: Map<string, number>;
  onInspect: (row: ThrustRow, index: number) => void;
  rowFindings?: RowFindings;
}) {
  return (
    <table className={extractionTableClass}>
      <thead className={extractionTheadClass}>
        <tr>
          {rowFindings && <FindingMarkerHeader />}
          {pageIdToPosition && <Th>Page</Th>}
          <Th>From</Th>
          <Th>To</Th>
          <Th>Date</Th>
          <Th>Authorizing Doc</Th>
          <Th>ESN</Th>
        </tr>
      </thead>
      <tbody className={extractionTbodyClass}>
        {rows.map((r, i) => (
          <tr
            key={`${r.pageId ?? 'page'}-${r.fromThrustRating}-${r.toThrustRating}-${i}`}
            onClick={() => onInspect(r, i)}
            className={extractionRowClass}
          >
            {rowFindings && <FindingMarkerCell findings={rowFindings.get(i)} />}
            {pageIdToPosition && (
              <Td>
                <SourcePageCell pageId={r.pageId} pageIdToPosition={pageIdToPosition} />
              </Td>
            )}
            <Td className="font-mono">{r.fromThrustRating || '—'}</Td>
            <Td className="font-mono">{r.toThrustRating || '—'}</Td>
            <Td>{r.conversionDate ?? '—'}</Td>
            <TruncatedTd value={r.authorizingDoc} />
            <Td className="font-mono">{r.esn ?? '—'}</Td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
